import { AxiosRequestConfig } from 'axios';
import c from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useToasts } from 'react-toast-notifications';
import Button from '../../components/Button';
import Error from '../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../components/Form';
import Header from '../../components/Header';
import Info from '../../components/Info';
import IconDocument from '../../components/icons/IconDocument';
import IconInvoice from '../../components/icons/IconInvoice';
import { StripeMode } from '../../types/pricingPage';
import { useAxios } from '../../utils/hooks';
import VALIDATORS from '../../utils/validators';

const MODES: FormData['mode'][] = [StripeMode.LIVE, StripeMode.TEST];

interface FormData {
    mode: StripeMode;
    invoiceId: string;
    companyName: string;
    companyAddress: string;
    companyCity: string;
    companyZip: string;
    companyCountry: string;
    ourCompanyName?: string;
    recieptNumber?: string;
    useInvoiceCompany: boolean;
    companyTaxId?: string;
}

interface Props {
    apiKey?: string;
}

export default function InvoiceGenerator({ apiKey }: Props) {
    const axios = useAxios();
    const { addToast } = useToasts();
    const [pdfData, setPdfData] = useState<Blob>();

    const axiosConfig: AxiosRequestConfig = useMemo(() => {
        const config: AxiosRequestConfig = { responseType: 'blob' };
        if (apiKey) {
            config.headers = { Authorization: `Bearer ${apiKey}` };
        }
        return config;
    }, [apiKey]);

    const handleGenerateInvoice = useCallback(
        (data: FormData) => {
            if (!axios || !axios.current) {
                return Promise.resolve();
            }
            setPdfData(undefined);
            return axios.current
                ?.post<Blob>('/invoices/generate', data, axiosConfig)
                .then((res) => {
                    setPdfData(res.data);
                })
                .catch((err) => {
                    if (err?.response?.status === 404) {
                        return { invoiceId: 'Invoice not found' };
                    }
                    console.error(err);
                    addToast('Something went wrong. Please try again.', { appearance: 'error' });
                });
        },
        [addToast, axios, axiosConfig]
    );

    return (
        <div className="w-full">
            <Header className="flex">
                <IconInvoice className="inline w-6 mr-3" /> Generate Stripe Invoice PDF
                <div className="ml-auto font-light">
                    <a
                        href="https://help.pricewell.com/features/invoices/generating-invoices/"
                        target="_blank"
                        rel="noreferrer"
                        className="link"
                    >
                        <IconDocument className="inline w-5 h-5 mr-1 align-text-bottom" />
                        Docs
                    </a>
                </div>
            </Header>
            <Form<FormData>
                initialValues={{ mode: StripeMode.LIVE, useInvoiceCompany: true }}
                onSubmit={handleGenerateInvoice}
            >
                {({ handleSubmit, submitting, hasValidationErrors, hasSubmitErrors, dirtySinceLastSubmit, values }) => (
                    <StyledForm onSubmit={handleSubmit} className="w-full mt-4 shadow bg-pattern">
                        <FormSection>
                            <FormSectionHeader>Stripe Invoice</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Stripe Mode</label>
                                    <div className="flex justify-start space-x-2 overflow-y-visible">
                                        {MODES.map((mode) => (
                                            <div className="form-check form-check-inline overflow-y-visible" key={mode}>
                                                <Field name="mode" type="radio" value={mode}>
                                                    {(props) => (
                                                        <label>
                                                            <input {...props.input} className="form-radio mr-2" />
                                                            <span
                                                                className={c(
                                                                    'form-check-label inline-block text-gray-800',
                                                                    {
                                                                        'text-green-500': mode === StripeMode.LIVE,
                                                                    }
                                                                )}
                                                            >
                                                                {mode}
                                                            </span>
                                                        </label>
                                                    )}
                                                </Field>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">
                                        Stripe Invoice (or PaymentIntent) ID{' '}
                                        <Info
                                            info="Click to learn how to find an invoice or payment id"
                                            href="https://help.pricewell.com/faq/find-stripe-invoice-id/"
                                        />
                                    </label>
                                    <Field
                                        name="invoiceId"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    placeholder="in_1MU1cXDMcRSELXUGw8Rll8ir"
                                                    className="input w-full"
                                                />
                                                {(meta.error || meta.submitError) && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error || meta.submitError} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Invoice Data</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">From Company (optional)</label>
                                    <Field name="ourCompanyName" component="input" type="text">
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="Your Co Ltd" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Company PO Number (optional)</label>
                                    <Field name="recieptNumber" component="input" type="text">
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="PO-1234" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Customer Address</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <Field name="useInvoiceCompany" component="input" type="text">
                                        {({ input, meta }) => (
                                            <>
                                                <label className="inline-flex items-center ml-2 text-sm">
                                                    <input
                                                        {...input}
                                                        type="checkbox"
                                                        className="checkbox"
                                                        defaultChecked={true}
                                                    />
                                                    <span className="text-gray-700 ml-2">
                                                        Use Customer Address from Stripe Invoice
                                                    </span>
                                                </label>
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                {!values.useInvoiceCompany && (
                                    <>
                                        <div className="mb-2">
                                            <label className="text-sm text-gray-600">Company Name</label>
                                            <Field
                                                name="companyName"
                                                component="input"
                                                type="text"
                                                validate={VALIDATORS.required}
                                            >
                                                {({ input, meta }) => (
                                                    <>
                                                        <input
                                                            {...input}
                                                            placeholder="Acme Co Ltd"
                                                            className="input w-full"
                                                        />
                                                        {meta.error && (
                                                            <span className="ml-2">
                                                                <Error message={meta.error} />
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                        <div className="mb-2">
                                            <label className="text-sm text-gray-600">Company Address Line 1</label>
                                            <Field
                                                name="companyAddress"
                                                component="input"
                                                type="text"
                                                validate={VALIDATORS.required}
                                            >
                                                {({ input, meta }) => (
                                                    <>
                                                        <input
                                                            {...input}
                                                            placeholder="1 The Street"
                                                            className="input w-full"
                                                        />
                                                        {meta.error && (
                                                            <span className="ml-2">
                                                                <Error message={meta.error} />
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                        <div className="mb-2">
                                            <label className="text-sm text-gray-600">Company Address City</label>
                                            <Field
                                                name="companyCity"
                                                component="input"
                                                type="text"
                                                validate={VALIDATORS.required}
                                            >
                                                {({ input, meta }) => (
                                                    <>
                                                        <input
                                                            {...input}
                                                            placeholder="San Francisco"
                                                            className="input w-full"
                                                        />
                                                        {meta.error && (
                                                            <span className="ml-2">
                                                                <Error message={meta.error} />
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                        <div className="mb-2">
                                            <label className="text-sm text-gray-600">
                                                Company Address Zip/Postal Code
                                            </label>
                                            <Field
                                                name="companyZip"
                                                component="input"
                                                type="text"
                                                validate={VALIDATORS.required}
                                            >
                                                {({ input, meta }) => (
                                                    <>
                                                        <input {...input} placeholder="90210" className="input block" />
                                                        {meta.error && (
                                                            <span className="ml-2">
                                                                <Error message={meta.error} />
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                        <div className="mb-2">
                                            <label className="text-sm text-gray-600">Company Address Country</label>
                                            <Field
                                                name="companyCountry"
                                                component="input"
                                                type="text"
                                                validate={VALIDATORS.required}
                                            >
                                                {({ input, meta }) => (
                                                    <>
                                                        <input {...input} placeholder="USA" className="input block" />
                                                        {meta.error && (
                                                            <span className="ml-2">
                                                                <Error message={meta.error} />
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                    </>
                                )}
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Customer Tax</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Tax Id (optional)</label>
                                    <Field name="companyTaxId" component="input" type="text">
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="DE123456789" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="flex">
                                    {pdfData && (
                                        <a
                                            href={URL.createObjectURL(pdfData)}
                                            download={`invoice-${values.invoiceId}.pdf`}
                                            className="group rounded-md shadow bg-green-500 text-white cursor-pointer flex justify-between items-center overflow-hidden transition-all hover:glow"
                                        >
                                            <div className="relative w-12 h-12 bg-gray-400 bg-opacity-20 text-white flex justify-center items-center transition-all">
                                                <svg
                                                    id="arrow"
                                                    className="w-4 h-4 transition-all group-hover:-translate-y-1"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                                    ></path>
                                                </svg>
                                            </div>
                                            <p className="px-5"> Download Invoice</p>
                                        </a>
                                    )}
                                    <Button
                                        type="submit"
                                        kind="tertiary"
                                        className="ml-auto"
                                        state={submitting ? 'SAVING' : 'READY'}
                                        disabled={hasValidationErrors || (hasSubmitErrors && !dirtySinceLastSubmit)}
                                    >
                                        Generate Invoice
                                    </Button>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                    </StyledForm>
                )}
            </Form>
        </div>
    );
}
